/* Sentry */
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, enableProdMode, ErrorHandler, LOCALE_ID, NgModule, provideZoneChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import * as Sentry from "@sentry/angular";

import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Router } from "@angular/router";
import { MAT_DATE_LOCALE } from '@angular/material/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

//  Gestione Overlay
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

/* UI Libraries */
import { MaterialModule } from './_commons/material.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgTemplateOutlet } from '@angular/common';
/* Plugins */
//  notifiche
import { SimpleNotificationsModule } from 'angular2-notifications';
import { ToastNotificationComponent } from './utility/toast-notification/toast-notification.component';
//  grafici
import { GoogleChartsModule } from 'angular-google-charts';
import { NgxEchartsModule } from 'ngx-echarts';
/* file viewer */
import { NgxDocViewerModule } from 'ngx-doc-viewer';

/* Localizzazione */
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeIT from '@angular/common/locales/it';
/* Providers */
import { HttpXsrfInterceptorInterceptor } from './_interceptors/http-xsrf-interceptor.interceptor';
import { BackendService } from './services/general/backend.service';

/* Componenti Helper */
import { Scroll } from './_helpers/scroll/scroll.component';

/* Direttive */
import { TrimValueAccessorDirective } from './_directives/trim-value-accessor.directive';
import { BackbuttonDirective } from './_directives/back-button.directive';
import { HasRoleDirective } from './_directives/has-role.directive';
import { DisableRoleDirective } from './_directives/disable-role.directive';

/* Pipes */
import { FormatdatePipe } from './_pipes/formatdate.pipe';
import { MinuteSecondsPipe } from './_pipes/minute-seconds.pipe';

/* Componenti principali */
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { BootstrapComponent } from './pages/bootstrap/bootstrap.component';

/* Modali */
import { ModalNewProposalComponent } from './modals/modal-new-proposal/modal-new-proposal.component';
import { ModalReportAttachmentComponent } from './modals/modal-report-attachment/modal-report-attachment.component';
import { ModalPrivacyPolicyComponent } from './modals/modal-privacy-policy/modal-privacy-policy.component';
import { ModalShowEvidencesComponent } from './modals/modal-show-evidences/modal-show-evidences.component';
import { ModalUserPermissionComponent } from './modals/modal-user-permissions/modal-user-permissions.component';
import { ModalYourOrganizationsComponent } from './modals/modal-your-organizations/modal-your-organizations.component';
import { ModalAddWorkExpComponent } from './modals/modal-add-work-exp/modal-add-work-exp.component';
import { ModalAddEducationComponent } from './modals/modal-add-education/modal-add-education.component';
import { ModalAddCertificateComponent } from './modals/modal-add-certificate/modal-add-certificate.component';
import { ModalProposalRenameComponent } from './modals/modal-proposal-rename/modal-proposal-rename.component';
import { ModalAddContestComponent } from './modals/modal-add-contest/modal-add-contest.component';
import { ModalShowContestComponent } from './modals/modal-show-contest/modal-show-contest.component';
import { ModalShowProposalsComponent } from './modals/modal-show-proposals/modal-show-proposals.component';
import { ModalTemplateEmailComponent } from './modals/modal-template-email/modal-template-email.component';
import { ModalEmailResponsesComponent } from './modals/modal-email-responses/modal-email-responses.component';

/* Allegati */
// import { PubmedAttachmentComponent } from './components/attachments/pubmed-attachment/pubmed-attachment.component'; // now standalone
// import { ViewerComponent } from "./pages/fileviewer/viewer.component";
import { FileViewerComponent } from "./components/fileviewer/file-viewer.component";

/* Componenti pagine */
import { HomeComponent } from './pages/home/home.component';
import { AllFrameworksComponent } from './pages/frameworks/all-frameworks/all-frameworks.component';
import { FrameworkComponent } from './pages/frameworks/framework/framework.component';
import { ResponsesComponent } from './pages/responses/responses.component';


import {
  NewDecisionTableSelectFrameworkComponent
} from './components/decision-table/new-decision-table/new-decision-table-select-framework/new-decision-table-select-framework.component';
import {
  NewDecisionTableCompileProposalComponent
} from './components/decision-table/new-decision-table/new-decision-table-compile-proposal/new-decision-table-compile-proposal.component';
import {
  NewDecisionTableCompileProblemStatementComponent
} from './components/decision-table/new-decision-table/new-decision-table-compile-problem-statement/new-decision-table-compile-problem-statement.component';
import {
  DecisionTableClosingComponent
} from './pages/decision-tables/decision-table-closing/decision-table-closing.component';
import {
  ShowDecisionTableComponent
} from './components/decision-table/show-decision-table/show-decision-table.component';

import { NewProposalComponent } from './components/proposals/new-proposal/new-proposal.component';
import { NewProposalBlankComponent } from './components/proposals/new-proposal-blank/new-proposal-blank.component';
import {
  MultiobjectiveNewProposalComponent
} from "./components/proposals/new-proposal/multiobjective/multiobjective-new-proposal.component";
import {
  MulticriteriaNewProposalComponent
} from "./components/proposals/new-proposal/multicriteria/multicriteria-new-proposal.component";
import {
  ConsensusNewProposalComponent
} from "./components/proposals/new-proposal/consensus/consensus-new-proposal.component";

import { ResponsesWelcomeComponent } from './pages/responses/responses-welcome/responses-welcome.component';
import { ResponsesSummaryComponent } from './pages/responses/responses-summary/responses-summary.component';
import {
  ResponsesWeightTargetsComponent
} from './pages/responses/responses-weight-targets/responses-weight-targets.component';
import {
  ResponsesWeightCriteriaComponent
} from './pages/responses/responses-weight-criteria/responses-weight-criteria.component';
import { ResponsesEvaluationComponent } from './pages/responses/responses-evaluation/responses-evaluation.component';
import {
  ResponsesProposalReviewComponent
} from './pages/responses/responses-proposal-review/responses-proposal-review.component';
import {
  ResponsesSuggestNewProposalComponent
} from './components/responses/responses-suggest-new-proposal/responses-suggest-new-proposal.component';
import { ResponsesThankYouComponent } from './pages/responses/responses-thank-you/responses-thank-you.component';
import { ResponsesExplainComponent } from './pages/responses/responses-explain/responses-explain.component';

import {
  MultiobjectiveResponsesSummaryComponent
} from './components/responses/multiobjective/multiobjective-responses-summary/multiobjective-responses-summary.component';
import {
  MultiobjectiveResponsesWeightTargetsComponent
} from './components/responses/multiobjective/multiobjective-responses-weight-targets/multiobjective-responses-weight-targets.component';
import {
  MultiobjectiveResponsesWeightCriteriaComponent
} from './components/responses/multiobjective/multiobjective-responses-weight-criteria/multiobjective-responses-weight-criteria.component';
import {
  MultiobjectiveResponsesEvaluationComponent
} from './components/responses/multiobjective/multiobjective-responses-evaluation/multiobjective-responses-evaluation.component';
import {
  MultiobjectiveResponsesExplainComponent
} from './components/responses/multiobjective/multiobjective-responses-explain/multiobjective-responses-explain.component';
import {
  MulticriteriaResponsesSummaryComponent
} from './components/responses/multicriteria/multicriteria-responses-summary/multicriteria-responses-summary.component';
import {
  MulticriteriaResponsesWeightCriteriaComponent
} from './components/responses/multicriteria/multicriteria-responses-weight-criteria/multicriteria-responses-weight-criteria.component';
import {
  MulticriteriaResponsesEvaluationComponent
} from './components/responses/multicriteria/multicriteria-responses-evaluation/multicriteria-responses-evaluation.component';
import {
  MulticriteriaResponsesExplainComponent
} from './components/responses/multicriteria/multicriteria-responses-explain/multicriteria-responses-explain.component';
import {
  ConsensusResponsesSummaryComponent
} from './components/responses/consensus/consensus-responses-summary/consensus-responses-summary.component';
import {
  ConsensusResponsesEvaluationComponent
} from './components/responses/consensus/consensus-responses-evaluation/consensus-responses-evaluation.component';
import {
  ConsensusResponsesExplainComponent
} from './components/responses/consensus/consensus-responses-explain/consensus-responses-explain.component';

import { DecisionTableComponent } from './pages/decision-tables/decision-table/decision-table.component';
import {
  DecisionTableDashboardComponent
} from './pages/decision-tables/decision-table-dashboard/decision-table-dashboard.component';
import {
  ConsensusDecisionTableDashboardComponent
} from "./components/decision-table/dashboard/consensus-decision-table-dashboard/consensus-decision-table-dashboard.component";
import {
  MultiobjectiveDecisionTableDashboardComponent
} from "./components/decision-table/dashboard/multiobjective-decision-table-dashboard/multiobjective-decision-table-dashboard.component";
import {
  MulticriteriaDecisionTableDashboardComponent
} from "./components/decision-table/dashboard/multicriteria-decision-table-dashboard/multicriteria-decision-table-dashboard.component";
import { AllDecisionTableComponent } from './pages/decision-tables/all-decision-table/all-decision-table.component';

import { environment } from 'src/environments/environment';
import { TaskManagementComponent } from './pages/task-management/task-management/task-management.component';
import {
  DecisionManagementPlanComponent
} from './pages/decision-management-plan/decision-management-plan/decision-management-plan.component';

import { OrganizationsComponent } from './pages/organizations/organizations.component';
import { OrganizationComponent } from './pages/organizations/organization/organization.component';
import {
  OrganizationManagementComponent
} from './pages/organizations/organization-management/organization-management.component';
import {
  OrganizationAddRoleComponent
} from './pages/organizations/organization-add-role/organization-add-role.component';
import { DefinitionRolesComponent } from './pages/definition-roles/definition-roles.component';
import { OrganizationalFunctionsComponent } from './pages/organizational-functions/organizational-functions.component';

import { AllUsersManagementComponent } from './pages/user/all-users-management/all-users-management.component';
import {
  AllDocumentsManagementComponent
} from './pages/organizations/all-documents-management/all-documents-management.component';
import {
  TemplateMailManagementComponent
} from './pages/organizations/template-mail-management/template-mail-management.component';
import { SingleUserManagementComponent } from './pages/user/single-user-management/single-user-management.component';
import { UserProfileComponent } from './pages/user/user-profile/user-profile.component';
import { CurriculumComponent } from './pages/user/curriculum/curriculum.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import { RegisterComponent } from './register/register.component';
import { LoginComponent } from './pages/login/login.component';
import { GuestComponent } from './pages/guest/guest.component';

import { OnboardingComponent } from './pages/onboarding/onboarding.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ChangePasswordComponent } from './pages/user/change-password/change-password.component';
import {
  NewContestProposalComponentComponent
} from './components/contests/new-contest-proposal-component/new-contest-proposal-component.component';
import {
  ShowContestComponentComponent
} from './components/contests/show-contest-component/show-contest-component.component';
import {
  OrganizationChartsManagementComponent
} from './pages/organizations/organization-charts-management/organization-charts-management.component';
import { ModalOrganizationChartComponent } from './modals/modal-organization-chart/modal-organization-chart.component';
import {
  ModalImportOrganizationChartsComponent
} from './modals/modal-import-organization-charts/modal-import-organization-charts.component';

import { ModalResetPasswordComponent } from './modals/modal-reset-password/modal-reset-password.component';
import { ModalChangePasswordComponent } from './modals/modal-change-password/modal-change-password.component';
import { ModalMagicWriterComponent } from './modals/modal-magic-writer/modal-magic-writer.component';
import {
  ModalDashboardAiExplanationComponent
} from './modals/modal-dashboard-ai-explanation/modal-dashboard-ai-explanation.component';
import { SingleTeamMemberComponent } from "./components/single-team-member/single-team-member.component";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { ModalEditWeightsComponent } from './modals/modal-edit-weights/modal-edit-weights.component';
import {
  CriteriaTargetsWeightUpdateComponent
} from './components/framework/criteria-targets-weight-update/criteria-targets-weight-update.component';
import { ProjectsComponent } from "./pages/projects/all-projects/projects.component";
import { ProjectDetailComponent } from './pages/projects/project-detail/project-detail.component';
import { ModalAddProjectComponent } from './modals/modal-add-project/modal-add-project.component';
import { AllSurveysComponent } from './components/surveys/all-surveys/all-surveys.component';
import { SurveyDetailsComponent } from './components/surveys/survey-details/survey-details.component';
import { SingleQuestionComponent } from './components/surveys/single-question/single-question.component';
import { QuestionQuantitativeComponent } from './components/surveys/single-question/question-quantitative/question-quantitative.component';
import { QuestionConsensusComponent } from './components/surveys/single-question/question-consensus/question-consensus.component';
import { QuestionOptionsComponent } from './components/surveys/single-question/question-options/question-options.component';
import { ModalSurveyDetailsComponent } from './modals/modal-survey-details/modal-survey-details.component';
import { SurveyDetailsPageComponent } from './pages/surveys/survey-details-page/survey-details-page.component';
import { TeamMembersListComponent } from './components/surveys/team-members-list/team-members-list.component';
import { MatTabsModule } from "@angular/material/tabs";
import { SurveyTeamComponent } from './components/surveys/survey-team/survey-team.component';
import { SurveyCustomFieldsFilterComponent } from './components/surveys/survey-custom-fields-filter/survey-custom-fields-filter.component';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SurveyResponsePageComponent } from './pages/surveys/survey-response-page/survey-response-page.component';
import { AttachmentsListComponent } from "./components/surveys/attachments-list/attachments-list.component";
import { SurveyAllResponsesComponent } from './components/surveys/survey-all-responses/survey-all-responses.component';
import {
  SingleQuestionResponsesComponent
} from './components/surveys/single-question-responses/single-question-responses.component';
import { ShowIfOrganizationDirective } from './_directives/show-if-organization.directive';

import { LoadingComponent } from './components/loading/loading.component';
import { GenericLoadingComponent } from './components/genericloading/generic-loading.component';

import { DragDropModule } from "@angular/cdk/drag-drop";
import {
  DecisionTableDetailsComponent
} from './components/decision-table/decision-table-details/decision-table-details.component';
import { HideIfOrganizationDirective } from './_directives/hide-if-organization.directive';
import { ModalAddInvitationPageComponent } from './modals/modal-add-invitation-page/modal-add-invitation-page.component';
import { RichTextEditorComponent } from 'src/app/components/rich-text-editor/rich-text-editor.component';
import { FormattedTextPreviewComponent } from 'src/app/components/rich-text-editor/formatted-text-preview.component';
import { DecisionJoinRequestComponent } from './pages/decision-join-request/decision-join-request.component';
import { ModalSelectProcessRoleComponent } from './modals/modal-select-process-role/modal-select-process-role.component';

import { QrCodeComponent } from './components/qrcode/qrcode.component';
import { ModalAddTextComponent } from './modals/modal-add-text/modal-add-text.component';
import { MarkdownModule } from "ngx-markdown";
import { ShowIfOrganizationHasFeatureDirective } from './_directives/show-if-organization-has-feature.directive';
import {
  SingleQuestionAiAnalysisComponent
} from './components/ai-analysis/single-question-ai-analysis/single-question-ai-analysis.component';
import { AiMagicWriterComponent } from './components/virtual-assistant-magic-writer/ai-magic-writer.component';
import { ModalShowSurveysComponent } from './modals/modal-show-surveys/modal-show-surveys.component';
import {
  ModalInvitationPagesSettingsComponent
} from './modals/modal-invitation-pages-settings/modal-invitation-pages-settings.component';
import {
  ProjectPropertyListComponent
} from './pages/project-properties/project-property-list/project-property-list.component';
import { PropertyCreateDialogComponent } from './modals/property-create-dialog/property-create-dialog.component';
import { UserNotesListComponent } from './pages/user-notes/user-notes-list/user-notes-list.component';
import { UserNoteDialogComponent } from './modals/user-note-dialog/user-note-dialog.component';
import {
  CkmSimulationListComponent
} from './components/ckm-simulations/ckm-simulation-list/ckm-simulation-list.component';
import { CkmSimulationComponent } from './components/ckm-simulations/ckm-simulation/ckm-simulation.component';
import { GenericListComponent } from './components/ckm-simulations/generic-list/generic-list.component';
import {
  CkmSimulationCreateStepperComponent
} from './components/ckm-simulations/ckm-simulation-create-stepper/ckm-simulation-create-stepper.component';
import {
  CkmSimulationCreateComponent
} from './modals/ckm-simulations/ckm-simulation-create/ckm-simulation-create.component';
import { ModalAddCkmSimulationComponent } from './modals/modal-add-ckm-simulation/modal-add-ckm-simulation.component';
import { AttachmentManagerComponent } from './components/attachment-manager/attachment-manager.component';
import {
  CkmSimulationDetailsComponent
} from './components/ckm-simulations/ckm-simulation/ckm-simulation-details/ckm-simulation-details.component';
import { NerfSimulatorComponent } from './components/nerf-simulator/nerf-simulator.component';
import {
  CkmSimulationMapsComponent
} from './components/ckm-simulations/ckm-simulation/ckm-simulation-maps/ckm-simulation-maps.component';
import { CkmMapDetailsComponent } from './components/ckm-maps/ckm-map-details/ckm-map-details.component';
import {
  CkmModalEntityDetailsComponent
} from './components/ckm-maps/ckm-modal-entity-details/ckm-modal-entity-details.component';
import { GridQuestionTableComponent } from './components/surveys/grid-question-table/grid-question-table.component';
import { SurveyDashboardComponent } from './components/surveys/survey-dashboard/survey-dashboard.component';
import { SurveyFinalDecisionSelectorComponent } from './components/surveys/survey-dashboard/survey-final-decision-selector/survey-final-decision-selector.component';
import { SurveyTreeComponent } from './components/surveys/survey-tree/survey-tree.component';
import {
  SurveyQuestionsDashboardComponent
} from './components/surveys/survey-questions-dashboard/survey-questions-dashboard.component';
import {
  QuestionHistoryDashboardComponent
} from './components/surveys/question-history-dashboard/question-history-dashboard.component';
import { IntegratedGradeTableComponent } from './components/grade/integrated-grade-table/integrated-grade-table.component';
import { ModalEditDocumentComponent } from './modals/modal-edit-document/modal-edit-document.component';
import { StatementGeneratorComponent, AnalysisDialogComponent } from './components/statement-generator/statement-generator.component';
import { StatementGeneratorFormComponent } from './components/statement-generator/statement-generator-form.component';
import { StatementProgressMonitorComponent } from './components/statement-generator/statement-progress-monitor.component';
import { StatementReviewComponent } from './components/statement-generator/statement-review.component';
import { StatementGenerationsListComponent } from './components/statement-generations-list/statement-generations-list.component';
import { StatementGenerationDetailsComponent } from './components/statement-generation-details/statement-generation-details.component';
import { ModalSelectDocumentsComponent } from './modals/modal-select-documents/modal-select-documents.component';
import { A11yModule } from "@angular/cdk/a11y";
import { ReplaceSpacesPipe } from './_pipes/replace-spaces.pipe';
import { ModalSurveyRevisionPreviewComponent } from './modals/modal-survey-revision-preview/modal-survey-revision-preview.component';
import { ModalSplitQuestionComponent } from './modals/modal-split-question/modal-split-question.component';
import { ModalReviseQuestionComponent } from './modals/modal-revise-question/modal-revise-question.component';
import { ModalViewSentSurveyQuestionnaireComponent } from './modals/modal-view-sent-survey-questionnaire/modal-view-sent-survey-questionnaire.component';
import { ModalShareQrComponent } from './modals/modal-share-qr/modal-share-qr.component';
import { ModalImportSurveyTeamComponent } from './modals/modal-import-survey-team/modal-import-survey-team.component';
import { FeedbackReportButtonComponent } from './components/feedback-report-button/feedback-report-button.component';
import { SurveyCustomChartsComponent } from './components/surveys/survey-custom-charts/survey-custom-charts.component';
import { SpeechToTextRecorderComponent } from './components/surveys/speech-to-text-recorder/speech-to-text-recorder.component';
// import localeITExtra from '@angular/common/locales/extra/it';
// registerLocaleData(localeIT, localeITExtra);
registerLocaleData(localeIT);

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  // return new TranslateHttpLoader(http, '/assets/i18n/', '.json?cache=' + environment.version);
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?v=' + environment.version);
  // return new TranslateHttpLoader(http);
}


@NgModule({
  declarations: [
    AppComponent,
    ToastNotificationComponent,
    TrimValueAccessorDirective,
    BackbuttonDirective,
    Scroll,
    FormatdatePipe,
    MinuteSecondsPipe,
    BootstrapComponent,
    HeaderComponent,
    FooterComponent,
    HomeComponent,
    ProjectsComponent,
    AllFrameworksComponent,
    FrameworkComponent,
    DecisionTableComponent,
    AllDecisionTableComponent,
    NewDecisionTableSelectFrameworkComponent,
    NewDecisionTableCompileProposalComponent,
    NewDecisionTableCompileProblemStatementComponent,
    ShowDecisionTableComponent,
    DecisionTableDashboardComponent,
    ConsensusDecisionTableDashboardComponent,
    MultiobjectiveDecisionTableDashboardComponent,
    MulticriteriaDecisionTableDashboardComponent,
    ModalNewProposalComponent,
    NewProposalComponent,
    NewProposalBlankComponent,
    MultiobjectiveNewProposalComponent,
    MulticriteriaNewProposalComponent,
    ConsensusNewProposalComponent,
    ModalAddWorkExpComponent,
    ModalAddEducationComponent,
    ModalAddCertificateComponent,
    ModalProposalRenameComponent,
    ModalShowEvidencesComponent,
    ModalUserPermissionComponent,
    ModalReportAttachmentComponent,
    ModalPrivacyPolicyComponent,
    ModalYourOrganizationsComponent,
    ModalAddContestComponent,
    ModalShowContestComponent,
    ModalShowProposalsComponent,
    ModalTemplateEmailComponent,
    ModalEmailResponsesComponent,
    ResponsesComponent,
    ResponsesWelcomeComponent,
    ResponsesSummaryComponent,
    ResponsesWeightTargetsComponent,
    ResponsesWeightCriteriaComponent,
    ResponsesEvaluationComponent,
    ResponsesProposalReviewComponent,
    ResponsesSuggestNewProposalComponent,
    ResponsesThankYouComponent,
    ResponsesExplainComponent,
    MultiobjectiveResponsesSummaryComponent,
    MultiobjectiveResponsesWeightTargetsComponent,
    MultiobjectiveResponsesWeightCriteriaComponent,
    MultiobjectiveResponsesEvaluationComponent,
    MultiobjectiveResponsesExplainComponent,
    MulticriteriaResponsesEvaluationComponent,
    MulticriteriaResponsesSummaryComponent,
    MulticriteriaResponsesWeightCriteriaComponent,
    MulticriteriaResponsesExplainComponent,
    ConsensusResponsesSummaryComponent,
    ConsensusResponsesEvaluationComponent,
    ConsensusResponsesExplainComponent,
    TaskManagementComponent,
    DecisionManagementPlanComponent,
    FormatdatePipe,
    // PubmedAttachmentComponent - now standalone, imported where used
    // ViewerComponent,
    FileViewerComponent,
    OrganizationsComponent,
    OrganizationComponent,
    OrganizationAddRoleComponent,
    OrganizationManagementComponent,
    DefinitionRolesComponent,
    OrganizationalFunctionsComponent,
    AllUsersManagementComponent,
    AllDocumentsManagementComponent,
    TemplateMailManagementComponent,
    SingleUserManagementComponent,
    UserProfileComponent,
    CurriculumComponent,
    ErrorPageComponent,
    RegisterComponent,
    LoginComponent,
    DecisionTableClosingComponent,
    OnboardingComponent,
    ChangePasswordComponent,
    NewContestProposalComponentComponent,
    ShowContestComponentComponent,
    OrganizationChartsManagementComponent,
    ModalOrganizationChartComponent,
    ModalImportOrganizationChartsComponent,
    ModalResetPasswordComponent,
    ModalChangePasswordComponent,
    SingleTeamMemberComponent,
    ModalEditWeightsComponent,
    CriteriaTargetsWeightUpdateComponent,
    GuestComponent,
    ProjectDetailComponent,
    ModalAddProjectComponent,
    AllSurveysComponent,
    SurveyDetailsComponent,
    SingleQuestionComponent,
    QuestionQuantitativeComponent,
    QuestionConsensusComponent,
    QuestionOptionsComponent,
    ModalSurveyDetailsComponent,
    SurveyDetailsPageComponent,
    TeamMembersListComponent,
    SurveyTeamComponent,
    SurveyCustomFieldsFilterComponent,
    SurveyResponsePageComponent,
    AttachmentsListComponent,
    SurveyAllResponsesComponent,
    SingleQuestionResponsesComponent,
    ShowIfOrganizationDirective,
    LoadingComponent,
    GenericLoadingComponent,
    ModalMagicWriterComponent,
    ModalDashboardAiExplanationComponent,
    DecisionTableDetailsComponent,
    HideIfOrganizationDirective,
    ModalAddInvitationPageComponent,
    RichTextEditorComponent,
    FormattedTextPreviewComponent,
    DecisionJoinRequestComponent,
    ModalSelectProcessRoleComponent,
    QrCodeComponent,
    ModalAddTextComponent,
    ShowIfOrganizationHasFeatureDirective,
    SingleQuestionAiAnalysisComponent,
    AiMagicWriterComponent,
    ModalShowSurveysComponent,
    ModalInvitationPagesSettingsComponent,
    ProjectPropertyListComponent,
    PropertyCreateDialogComponent,
    UserNotesListComponent,
    UserNoteDialogComponent,
    CkmSimulationListComponent,
    CkmSimulationComponent,
    GenericListComponent,
    CkmSimulationCreateStepperComponent,
    CkmSimulationCreateComponent,
    ModalAddCkmSimulationComponent,
    NerfSimulatorComponent,
    AttachmentManagerComponent,
    CkmSimulationDetailsComponent,
    CkmSimulationMapsComponent,
    CkmMapDetailsComponent,
    CkmModalEntityDetailsComponent,
    GridQuestionTableComponent,
    SurveyDashboardComponent,
    SurveyFinalDecisionSelectorComponent,
    SurveyTreeComponent,
    SurveyQuestionsDashboardComponent,
    QuestionHistoryDashboardComponent,
    IntegratedGradeTableComponent,
    ModalEditDocumentComponent,
    StatementGeneratorComponent,
    AnalysisDialogComponent,
    StatementGeneratorFormComponent,
    StatementProgressMonitorComponent,
    StatementReviewComponent,
    StatementGenerationsListComponent,
    StatementGenerationDetailsComponent,
    ModalSelectDocumentsComponent,
    ReplaceSpacesPipe,
    ModalSurveyRevisionPreviewComponent,
    ModalSplitQuestionComponent,
    ModalReviseQuestionComponent,
    ModalViewSentSurveyQuestionnaireComponent,
    ModalShareQrComponent,
    ModalImportSurveyTeamComponent,
    SpeechToTextRecorderComponent,
    SurveyCustomChartsComponent,
    HasRoleDirective,
    DisableRoleDirective],
  bootstrap: [AppComponent],
  exports: [
    SurveyDetailsComponent
  ],
  imports: [
    BrowserModule,
    FeedbackReportButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
    NgxDocViewerModule,
    SimpleNotificationsModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    NgbModule,
    GoogleChartsModule,
    FontAwesomeModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    DragDropModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    MarkdownModule.forRoot(),
    A11yModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
    { provide: Sentry.TraceService, deps: [Router] },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpXsrfInterceptorInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    BackendService,
    provideHttpClient(withInterceptorsFromDi()),
  ]
})
export class AppModule {
}
